import React, {useState, useEffect} from "react";
import {
  Button,
  TextField,
  Stack,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormControlLabel,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider
} from "@mui/material";
import Modal from "components/shared/Modal";
import LoaderButton from "components/shared/LoaderButton";
import {getEmailFieldError} from "utils/validationUtils";
import cognito_groups from "constants/cognito_groups";
import ORGANIZATION_TYPES from "constants/organization_types";

export default function UserDetailsModal ({
  open, 
  onClose, 
  onSubmit, 
  loading = false, 
  user, 
  roles = [], 
  vendors = [],
  vendorLabel = "Vendor",
}) {
  const [userData, setUserData] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [selectedVendors, setSelectedVendors] = useState([]);
  const hasAssociatedSubcontractors = userData.role === cognito_groups.ClientPMRole || userData.role === cognito_groups.Subcontractor;
  
  const isAllSelected = vendors?.length > 0 && selectedVendors.length === vendors.length;

  useEffect(() => {
    if(user) {
      setUserData({
        ...user,
        role: user.role || roles[0]?.value
      });
      // If user has associated vendors, preselect them
      if (user.assignedVendors) {
        setSelectedVendors(user.assignedVendors);
      }
    } else {
      setUserData({
        role: roles[0]?.value
      })
    }
  }, [user, roles])

  useEffect(() => {
    if(!open) {
      setUserData({})
      setFormErrors({})
      setSelectedVendors([]);
    }
  }, [open])

  function updateUserField(event, trim=false, skipValidation=false) {
    const key = event.target.name
    let value = event.target.value
    
    if(!skipValidation && key === "email") {
      value = String(event.target.value).trim().toLowerCase()
      
      const newFormErrors = {...formErrors}
      
      // Clear error first
      delete newFormErrors.email
      
      // Only validate if there's some input
      if (value) {
        // First check basic email format
        const basicEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!basicEmailPattern.test(value)) {
          newFormErrors.email = "Please enter a valid email address"
        } 
        // Then check Cognito-compatible pattern
        else {
          const cognitoPattern = /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+@[\p{L}\p{M}\p{S}\p{N}\p{P}]+\.[\p{L}\p{N}]+$/u
          if (!cognitoPattern.test(value)) {
            newFormErrors.email = "Email contains characters not supported by the system"
          }
        }
      }
      
      setFormErrors(newFormErrors)
    }
    setUserData({
      ...userData,
      [key]: trim ? String(value).trim() : value
    })
  }

  function validateForm(event) {
    const key = event.target.name
    let value = String(event.target.value).trim()
    
    const newFormErrors = {...formErrors}
    
    if(key === "email") {
      // Clear error first
      delete newFormErrors.email
      
      if (value) {
        // First check basic email format
        const basicEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!basicEmailPattern.test(value)) {
          newFormErrors.email = "Please enter a valid email address"
        } 
        // Then check Cognito-compatible pattern
        else {
          const cognitoPattern = /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+@[\p{L}\p{M}\p{S}\p{N}\p{P}]+\.[\p{L}\p{N}]+$/u
          if (!cognitoPattern.test(value)) {
            newFormErrors.email = "Email contains characters not supported by the system"
          }
        }
      }
      
      setFormErrors(newFormErrors)
    }
  }

  function handleVendorToggle(vendorId) {
    const currentIndex = selectedVendors.indexOf(vendorId);
    const newSelectedVendors = [...selectedVendors];

    if (currentIndex === -1) {
      newSelectedVendors.push(vendorId);
    } else {
      newSelectedVendors.splice(currentIndex, 1);
    }

    setSelectedVendors(newSelectedVendors);
  }

  function handleAllVendorsToggle() {
    if (isAllSelected) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(vendors.map(vendor => vendor.id));
    }
  }

  function handleSubmit() {
    const submissionData = { ...userData };
    if (hasAssociatedSubcontractors) {
      submissionData.assignedVendors = selectedVendors;
    }
    onSubmit && onSubmit(submissionData);
  }

  const fieldProps = {
    onChange: updateUserField,
    onBlur: validateForm,
    fullWidth: true
  }

  return (
    <Modal
      open={open}
      title={!!user ? "Update User" : "Add User"}
      className="add-user-modal"
      content={
        <Stack spacing={2} mt={2}>
          <TextField
            {...fieldProps}
            name="email"
            required
            label={"Email Address"}
            className="add-user-email-field"
            inputProps={{
              pattern: "^[\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+@[\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+\\.[\\p{L}\\p{N}]+$"
            }}
            error={!!formErrors.email}
            helperText={formErrors.email || "This is also the username they will use to log in."}
            disabled={!!user}
            value={userData.email}
          />
          <Stack direction="row" spacing={2} className="add-user-optional-fields">
            <TextField
              {...fieldProps}
              name="firstName"
              label="First Name"
              value={userData.firstName}
            />
            <TextField
              {...fieldProps}
              name="lastName"
              label="Last Name"
              value={userData.lastName}
            />
          </Stack>
          <FormControl className="add-user-role-field">
            <FormLabel id="roles-group-label">User Role</FormLabel>
            <RadioGroup
              {...fieldProps}
              row
              aria-labelledby="roles-group-label"
              defaultValue={user?.role || roles[0]?.value}
              name="role"
            >
              {
                roles && roles.map(role => (
                  <FormControlLabel value={role.value} control={<Radio />} label={role.label} key={role.value}/>
                ))
              }
            </RadioGroup>
          </FormControl>

          {/* Vendor Selection for PM Role */}
          {hasAssociatedSubcontractors && vendors && vendors.length > 0 && (
            <Box mt={2}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {vendorLabel} Assignment
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <ListItem
                  dense
                  divider
                  sx={{ 
                    bgcolor: 'action.hover',
                    fontWeight: 'bold'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        edge="start"
                        checked={isAllSelected}
                        onChange={handleAllVendorsToggle}
                      />
                    }
                    label={<Typography sx={{ fontWeight: 'bold' }}>All {vendorLabel}s</Typography>}
                  />
                </ListItem>
                
                {/* Individual Vendors */}
                {vendors.map((vendor) => (
                  <ListItem key={vendor.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          edge="start"
                          checked={selectedVendors.indexOf(vendor.id) !== -1}
                          onChange={() => handleVendorToggle(vendor.id)}
                        />
                      }
                      label={vendor.vendor.name}
                    />
                  </ListItem>
                ))}
                {vendors.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No vendors available" />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </Stack>
      }
      actions={
        <>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <LoaderButton
            loading={loading}
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !userData.email || Object.keys(formErrors).length}
          >
            {
              !!user ? "Update" : "Invite User"
            }
          </LoaderButton>
        </>
      }
    />
  )
}